import { useEffect, useMemo, useState } from 'react'

import { getProjectDashboardCards } from '@infura/packages.contentful'
import { ActionLink } from '@infura/packages.ui/ActionLink'
import { Heading } from '@infura/packages.ui/Heading'
import { Tag } from '@infura/packages.ui/Tag'
import { Text } from '@infura/packages.ui/Text'

import SlickGallery from 'components/ui/SlickGallery'

interface ProductCardProps {
  isDisabled?: boolean
  tag: string
  title: string
  description: string
  linkText: string
  linkUrl: string
  maxWidth?: boolean
}

interface ProjectDashboardCard {
  __typename?: 'ProjectDashboardCard'
  isDisabled?: boolean
  tag?: string
  title?: string
  description?: string
  linkText?: string
  linkUrl?: string
  order?: number
}

const sortByOrder = (arr: Array<any>) => {
  const sortedArr = [...arr]
  sortedArr.sort((a, b) => {
    const orderA = a.order !== undefined && a.order !== null ? a.order : Infinity
    const orderB = b.order !== undefined && b.order !== null ? b.order : Infinity
    if (orderA === null && orderB === null) {
      return 0
    } else if (orderA === null) {
      return 1
    } else if (orderB === null) {
      return -1
    } else {
      return orderA - orderB
    }
  })
  return sortedArr
}

const ProductCard = ({ description, linkText, linkUrl, maxWidth, tag, title }: ProductCardProps) => {
  const maxWidthLg = maxWidth ? 'max-w-[290px]' : ''

  return (
    <div
      className={`bg-background-alternative text-text-default flex h-full min-h-[311px] w-full flex-col justify-between overflow-hidden rounded-lg bg-cover bg-no-repeat p-6 xl:min-w-[290px] ${maxWidthLg}`}
    >
      <div className='w-full'>
        <Tag className='mb-4 capitalize'>{tag}</Tag>
        <Heading as='h4' className='mb-4'>
          {title}
        </Heading>
        <Text className='mb-4 line-clamp-5'>{description}</Text>
      </div>
      <div className='w-full'>
        <ActionLink href={linkUrl} hrefTarget='_blank'>
          {linkText}
        </ActionLink>
      </div>
    </div>
  )
}

export const ProductUpdatesSection = () => {
  const [productCards, setProductCards] = useState<Array<ProjectDashboardCard>>([])

  useEffect(() => {
    getProjectDashboardCards(true)
      .then((res): void => {
        if (res.__typename === 'ProjectDashboardCardCollection' && res?.items) {
          const sortedCards = sortByOrder(res.items)
          setProductCards(sortedCards)
        } else {
          setProductCards([])
        }
      })
      .catch(() => {
        setProductCards([])
      })
  }, [])

  const defaultSettingsGallery = {
    adaptiveHeight: true,
    centerMode: true,
    centerPadding: '30px',
    dots: true,
    infinite: false,
    slidesToScroll: 1,
    slidesToShow: 1,
    speed: 500,
    swipeToSlide: true
  }

  const GalleryComponent = useMemo(() => {
    if (productCards.length === 0) return null
    const cards = productCards.filter(product => !product.isDisabled)
    const settingsGallery = { ...defaultSettingsGallery }
    if (cards.length < 5) {
      return (
        <>
          <div className='hidden sm:grid sm:grid-cols-2 sm:gap-6 lg:grid-cols-4'>
            {cards.map((item, i) => (
              <div className='flex justify-center' key={`item-${i}`}>
                {/* @ts-expect-error enabled to support strictNullChecks - Please fix this if you can! */}
                <ProductCard {...item} />
              </div>
            ))}
          </div>
          <div className='-mx-6 sm:hidden'>
            <SlickGallery
              classes='md:-mx-10'
              galleryItems={cards.map((item, i) => (
                <div className='h-full p-2' key={`product-card-${i}`}>
                  {/* @ts-expect-error enabled to support strictNullChecks - Please fix this if you can! */}
                  <ProductCard {...item} />
                </div>
              ))}
              settings={settingsGallery}
            />
          </div>
        </>
      )
    }

    return (
      <SlickGallery
        classes='-mr-10'
        galleryItems={cards.map((item, i) => (
          <div className='h-full p-2' key={`product-card-${i}`}>
            {/* @ts-expect-error enabled to support strictNullChecks - Please fix this if you can! */}
            <ProductCard {...item} maxWidth={true} />
          </div>
        ))}
        settings={{
          ...settingsGallery,
          arrows: false,
          centerMode: false,
          infinite: false,
          variableWidth: true
        }}
      />
    )
  }, [productCards])

  if (productCards.length === 0) return null

  return (
    <div className='border-border-muted bg-background-default m-auto w-full max-w-screen-2xl overflow-hidden rounded-md border p-6'>
      <Heading className='mb-4' theme='page'>
        Product updates
      </Heading>
      <div className='pb-6'>{GalleryComponent}</div>
    </div>
  )
}
